import React from 'react';

import { formatAmountValue, fractionToPercent, pluralize, sqlDateToLabel } from 'helpers/helpers';
import useTableStyles from 'apps/Pdf/styles/tables';
//import useGeneralStyles from 'apps/Pdf/styles/general';
import { AmountValue } from 'common/interfaces';
import {
  ExternalProductData,
  ExternalProductsData,
  ExtProductCategory,
  ExtProductFreq,
  ExtProductUsage,
  ExtProductType,
  SimpleDeposit,
  ExtProdSortBy,
} from 'pages/Accounts/Edit/components/ExternalProducts/model';
import {
  sortExtProducts,
  sortByTitle,
  sortByType,
  sortByFinInstTitle,
  sortByBalance,
  sortByPeriodic,
  sortByInterest,
  sortByLastUpdate,
  sortByUsage,
  sortByFrequency,
  sortByUntil,
  sortByAmount,
} from 'pages/Accounts/Edit/components/ExternalProducts/sorting';

import { useTranslation } from 'react-i18next';

interface Props {
  data: ExternalProductsData;
  currentAccount: { title: string; balance: AmountValue; profit: number };
}

const sortTable = (a: Partial<ExternalProductData>, b: Partial<ExternalProductData>, index: number, dir: number) => {
  switch (index) {
    case 0:
      return sortByTitle(a, b) * dir;
    case 1:
      return sortByType(a, b) * dir;
    case 2:
      return sortByFinInstTitle(a, b) * dir;
    case 3:
      return sortByBalance(a, b) * dir;
    case 4:
      return sortByPeriodic(a, b) * dir;
    case 5:
      return sortByInterest(a, b) * dir;
    case 6:
      return sortByLastUpdate(a, b) * dir;
    case 7:
      return sortByUsage(a, b) * dir;
    case 8:
      return sortByFrequency(a, b) * dir;
    case 9:
      return sortByUntil(a, b) * dir;
    case 10:
      return sortByAmount(a, b) * dir;

    default:
      return 0;
  }
};

const CashMovementsTable = (props: Props) => {
  //const styles = useGeneralStyles();
  const tables = useTableStyles();
  const { t } = useTranslation();

  const tableCategory = (products: Partial<ExternalProductData>[]) => {
    return (
      products.length > 0 && (
        <>
          {products
            .sort((a, b) => {
              return props.data.sort.index
                ? sortTable(a, b, props.data.sort.index, props.data.sort.direction ? 1 : -1)
                : sortExtProducts(a, b, ExtProdSortBy.INST, true);
            })
            .map((p, i) => {
              return (
                <tr key={p.id} style={{ backgroundColor: i % 2 === 0 && p.id !== 'summary' ? '#f6f6f6' : '' }}>
                  <td>
                    <span style={p.id === 'summary' ? { fontWeight: 'bold', fontSize: '14px' } : undefined}>
                      {p.title}
                    </span>
                  </td>
                  <td>{p.financialInstitution?.title}</td>
                  <td style={{ textAlign: 'right', paddingRight: '8px' }}>
                    <span style={p.id === 'summary' ? { fontWeight: 'bold', fontSize: '14px' } : undefined}>
                      {p.balance
                        ? formatAmountValue(
                            Math.round(p.balance.value) * (p.category === ExtProductCategory.LOAN ? -1 : 1),
                            p.balance.currency,
                          )
                        : ''}
                    </span>
                  </td>
                  <td>
                    {!p.periodic ? (
                      ''
                    ) : (
                      <span style={p.id === 'summary' ? { fontWeight: 'bold', fontSize: '14px' } : undefined}>
                        {p.periodic?.value && p.periodic.value.value > 0 && (
                          <>
                            {formatAmountValue(Math.round(p.periodic.value.value), p.periodic.value.currency)}{' '}
                            {!p.periodic.aggregated && p.category !== ExtProductCategory.LOAN && (
                              <>({t('general_client').toLowerCase()})</>
                            )}
                          </>
                        )}
                        {p.employer?.value && p.employer.value.value > 0 && (
                          <>
                            <br />
                            {formatAmountValue(Math.round(p.employer.value.value), p.employer.value.currency)} (
                            {t('general_employer').toLowerCase()})
                          </>
                        )}
                        {p.government?.value && p.government.value.value > 0 && (
                          <>
                            <br />
                            {formatAmountValue(Math.round(p.government.value.value), p.government.value.currency)} (
                            {t('externalProduct_government_short').toLowerCase()})
                          </>
                        )}
                      </span>
                    )}
                  </td>
                  <td>{p.interest !== undefined ? fractionToPercent(p.interest) : ''}</td>
                  <td>
                    {p.usage && p.category !== ExtProductCategory.LOAN
                      ? p.frequency === ExtProductFreq.INFINITE
                        ? t(`externalProduct_freq_${ExtProductFreq.INFINITE}`)
                        : t(`externalProduct_usage_${p.usage}`)
                      : ''}
                  </td>
                  <td>
                    {p.frequency ? (
                      <>
                        {p.frequency !== ExtProductFreq.INFINITE && p.usage !== ExtProductUsage.NONE && (
                          <>
                            {t(`externalProduct_freq_${p.frequency}`)}
                            <br />
                            {p.frequency === ExtProductFreq.REPEAT_EVERY && (
                              <>
                                {' '}
                                {p.repeatYears}
                                {' '}
                                {pluralize(p.repeatYears || 0, t('general_repeatYear'), t('general_repeatYears'))}
                              </>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {p.frequency ? (
                      <>
                        {p.frequency !== ExtProductFreq.INFINITE && p.usage !== ExtProductUsage.NONE && (
                          <>
                            {![
                              ExtProductFreq.MONTHLY,
                              ExtProductFreq.YEARLY,
                              ExtProductFreq.REPEAT_EVERY,
                              ExtProductFreq.PENSION,
                              ExtProductFreq.ADVANCE_PENSION,
                            ].includes(p.frequency) ? (
                              <>{sqlDateToLabel(p.since || '')}</>
                            ) : (
                              <>
                                {t('general_since')}: {sqlDateToLabel(p.since || p.periodic?.since || '')}
                              </>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      ''
                    )}
                  </td>

                  <td>{p.value && p.value.value > 0 ? formatAmountValue(p.value.value, p.value.currency) : ''}</td>
                </tr>
              );
            })}
        </>
      )
    );
  };

  return (
    <>
      {props.data ? (
        <>
          <table className={`${tables.root} ${tables.extProds}`}>
            <thead>
              <tr>
                <th>{t('general_title')}</th>
                {/*<th>{t('externalProduct_type')}</th>*/}
                <th>{t('externalProduct_financialInstitution_short')}</th>
                <th style={{ textAlign: 'right', paddingRight: '8px' }}>{t('accountPlan_currentValue')}</th>
                <th>{t('externalProduct_periodic_short')}</th>
                <th>{t('bucket_calculatedOutcome')}</th>
                <th>{t('externalProduct_usage')}</th>
                <th>{t('general_frequency')}</th>
                <th>{t('externalProduct_table_until')}</th>
                <th>{t('general_amount')}</th>
              </tr>
            </thead>
            <tbody>
              {tableCategory([
                {
                  title: props.currentAccount.title,
                  id: 'current',
                  periodic: { value: props.data?.currentDeposits, aggregated: true } as SimpleDeposit | undefined,
                  balance: props.currentAccount.balance,
                  interest: props.currentAccount.profit,
                  financialInstitution: { title: 'Edward', internal: true, id: '' },
                  type: ExtProductType.CURRENT,
                  frequency: ExtProductFreq.INFINITE,
                },
                ...props.data.externalProducts,
              ])}

              {tableCategory([
                {
                  title: t('externalProduct_table_sum'),
                  id: 'summary',
                  periodic: { value: props.data?.summary.deposit, aggregated: true } as SimpleDeposit | undefined,
                  balance: props.data?.summary.balance,
                },
              ])}
            </tbody>
          </table>
        </>
      ) : null}
    </>
  );
};

export default CashMovementsTable;
